/**
 * Candidates Pipeline Component
 * Admin → Recruitment → Candidates
 * 
 * Features:
 * - Table view with stage filters
 * - Add/Edit candidates
 * - Move through pipeline stages
 * - Add interview notes
 * - Resume upload
 * - Convert to Employee (Admin only)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { isHrReadOnly } from '../../../lib/hrRole';
import axios from 'axios';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Plus, Loader2, UserPlus, Download } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../ui/dialog';
import { toast } from 'sonner';
import RecruitmentNav from './RecruitmentNav';

import CandidateDetailDrawer from './CandidateDetailDrawer';
import { StageSummary, FilterBar, CandidatesTable, PaginationFooter } from './CandidatesWorkspace';
import { OptionalProfileSection, EMPTY_PROFILE, profileToPayload, validateProfile } from './CandidateProfileFields';

const API = process.env.REACT_APP_BACKEND_URL || '';
const currentRole = () => { try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch { return ''; } };

const Candidates = () => {
  const hrReadOnly = isHrReadOnly();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialJobId = searchParams.get('job_id') || '';
  
  // Data state
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [jobFilter, setJobFilter] = useState(initialJobId);

  // Hover state

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [drawerCandidateId, setDrawerCandidateId] = useState(null);
  const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
  const [profileForm, setProfileForm] = useState(EMPTY_PROFILE);
  const canViewCtc = ['admin', 'hr_manager'].includes(currentRole());
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [formData, setFormData] = useState({
    job_id: '',
    name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Convert to Employee form
  const [convertData, setConvertData] = useState({
    employee_id: '',
    date_of_joining: new Date().toISOString().split('T')[0],
    work_location: 'office',
    employee_timezone: 'UTC'
  });

  // Interview note

  // Stage options
  const stageOptions = [
    { value: 'applied', label: 'Applied', color: 'bg-slate-100 text-slate-700 border-slate-200' },
    { value: 'screening', label: 'Screening', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { value: 'interview', label: 'Interview', color: 'bg-purple-50 text-purple-700 border-purple-200' },
    { value: 'offer', label: 'Offer', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    { value: 'hired', label: 'Hired', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-50 text-red-600 border-red-200' }
  ];

  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState('newest');
  const [editRequestKey, setEditRequestKey] = useState(0);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Fetch jobs for dropdown
  const fetchJobs = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/api/recruitment/jobs?limit=100`, { headers });
      setJobs(response.data.data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  }, []);

  // Fetch candidates
  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (sort !== 'newest') params.append('sort', sort);
      if (searchQuery) params.append('search', searchQuery);
      if (stageFilter) params.append('stage', stageFilter);
      if (jobFilter) params.append('job_id', jobFilter);

      const response = await axios.get(`${API}/api/recruitment/candidates?${params.toString()}`, { headers });
      
      setCandidates(response.data.data || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.pages || 1);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      if (err.response?.status === 403) {
        toast.error('Access denied');
      } else {
        toast.error('Failed to load candidates');
      }
    } finally {
      setLoading(false);
    }
  }, [page, limit, sort, searchQuery, stageFilter, jobFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API}/api/recruitment/stats`, { headers });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    fetchStats();
  }, [fetchJobs, fetchStats]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Get candidate details
  const handleViewCandidate = (candidate) => {
    setSelectedCandidate(candidate);
    setDrawerCandidateId(candidate.candidate_id);
  };

  // Create candidate handlers
  const handleCreate = () => {
    setFormData({
      job_id: jobFilter || '',
      name: '',
      email: '',
      phone: '',
      notes: ''
    });
    setFormErrors({});
    setProfileForm(EMPTY_PROFILE);
    setIsCreateModalOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.job_id) errors.job_id = 'Job is required';
    if (!formData.name?.trim()) errors.name = 'Name is required';
    if (!formData.email?.trim()) errors.email = 'Email is required';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitCandidate = async () => {
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      const headers = getAuthHeaders();
      
      const profileErr = validateProfile(profileForm);
      if (profileErr) { toast.error(profileErr); setSubmitting(false); return; }
      await axios.post(`${API}/api/recruitment/candidates`, { ...formData, ...profileToPayload(profileForm, canViewCtc) }, { headers });
      toast.success('Candidate added successfully');
      setProfileForm(EMPTY_PROFILE);

      setIsCreateModalOpen(false);
      fetchCandidates();
      fetchStats();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to add candidate';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Convert to employee
  const handleConvert = async () => {
    if (!convertData.employee_id?.trim()) {
      toast.error('Employee ID is required.');
      return;
    }
    try {
      setSubmitting(true);
      const headers = getAuthHeaders();
      
      const params = new URLSearchParams();
      params.append('employee_id', convertData.employee_id.trim());
      params.append('date_of_joining', convertData.date_of_joining);
      params.append('work_location', convertData.work_location);
      params.append('employee_timezone', convertData.employee_timezone);
      
      const response = await axios.post(
        `${API}/api/recruitment/candidates/${selectedCandidate.candidate_id}/convert-to-employee?${params.toString()}`,
        {},
        { headers }
      );
      
      toast.success(`Converted to employee: ${response.data.employee_id}`);
      setIsConvertModalOpen(false);
      setDrawerRefreshKey(k => k + 1);
      fetchCandidates();
      fetchStats();
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to convert to employee';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (searchQuery) params.append('search', searchQuery);
      if (stageFilter) params.append('stage', stageFilter);
      if (jobFilter) params.append('job_id', jobFilter);
      const response = await axios.get(`${API}/api/recruitment/candidates/export?${params.toString()}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(response.data);
      a.download = `recruitment_export_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Recruitment data exported (${format.toUpperCase()})`);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-6">
      <RecruitmentNav />

      <div className="flex flex-wrap items-start justify-between gap-3" data-testid="candidates-header">
        <div>
          <h1 className="sf-heading text-2xl font-bold tracking-tight text-slate-900">Candidates</h1>
          <p className="mt-0.5 text-sm text-slate-500">Track and manage the candidate pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport('csv')} className="h-9 gap-2" data-testid="export-candidates-csv-btn"><Download className="w-4 h-4" /> CSV</Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('xlsx')} className="h-9 gap-2" data-testid="export-candidates-xlsx-btn"><Download className="w-4 h-4" /> Excel</Button>
          {!hrReadOnly && (
            <Button size="sm" onClick={handleCreate} className="h-9 gap-2 bg-blue-600 shadow-sm hover:bg-blue-700" data-testid="add-candidate-btn"><Plus className="w-4 h-4" /> Add Candidate</Button>
          )}
        </div>
      </div>

      <StageSummary byStage={stats?.candidates?.by_stage} active={stageFilter} onSelect={(s) => { setStageFilter(s); setPage(1); }} />

      <FilterBar search={searchQuery} onSearch={setSearchQuery} jobs={jobs} job={jobFilter} onJob={(v) => { setJobFilter(v); setPage(1); }}
        stage={stageFilter} onStage={(v) => { setStageFilter(v); setPage(1); }} sort={sort} onSort={(v) => { setSort(v); setPage(1); }} />

      <CandidatesTable candidates={candidates} loading={loading} canEdit={!hrReadOnly} onView={handleViewCandidate}
        onEdit={(c) => { handleViewCandidate(c); setEditRequestKey((k) => k + 1); }}
        onPipeline={() => navigate('/admin/recruitment/kanban')}
        onConvert={(c) => { setSelectedCandidate(c); setIsConvertModalOpen(true); }} />

      <PaginationFooter page={page} pages={totalPages} total={total} limit={limit} onPage={setPage} onLimit={(n) => { setLimit(n); setPage(1); }} />


      {/* Add Candidate Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-candidate-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Add Candidate</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Job Opening <span className="text-red-500">*</span>
              </label>
              <Select value={formData.job_id} onValueChange={(v) => setFormData(prev => ({ ...prev, job_id: v }))}>
                <SelectTrigger className={formErrors.job_id ? 'border-red-500' : ''} data-testid="add-candidate-job-select">
                  <SelectValue placeholder="Select job" />
                </SelectTrigger>
                <SelectContent>
                  {jobs.filter(j => j.status === 'open').map(j => (
                    <SelectItem key={j.job_id} value={j.job_id}>{j.title} ({j.team})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formErrors.job_id && <p className="text-xs text-red-500 mt-1">{formErrors.job_id}</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Full Name <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Doe"
                className={formErrors.name ? 'border-red-500' : ''}
                data-testid="add-candidate-name-input"
              />
              {formErrors.name && <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@email.com"
                className={formErrors.email ? 'border-red-500' : ''}
                data-testid="add-candidate-email-input"
              />
              {formErrors.email && <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 (555) 123-4567"
                data-testid="add-candidate-phone-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Notes</label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Initial notes..."
                data-testid="add-candidate-notes-input"
              />
            </div>
            <OptionalProfileSection value={profileForm} onChange={setProfileForm} showCtc={canViewCtc} />
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmitCandidate} disabled={submitting} data-testid="add-candidate-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Candidate Modal */}
      <CandidateDetailDrawer
        candidateId={drawerCandidateId}
        refreshKey={drawerRefreshKey}
        editRequestKey={editRequestKey}
        navIds={candidates.map(c => c.candidate_id)}
        onNavigate={(id) => { const c = candidates.find(x => x.candidate_id === id); if (c) setSelectedCandidate(c); setDrawerCandidateId(id); }}
        onClose={() => setDrawerCandidateId(null)}
        onChanged={() => { fetchCandidates(); fetchStats(); }}
        onConvert={(c) => { setSelectedCandidate(c); setIsConvertModalOpen(true); }}
      />

      {/* Convert to Employee Modal */}
      <Dialog open={isConvertModalOpen} onOpenChange={setIsConvertModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl">Convert to Employee</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <p className="text-sm text-blue-800">
                This will create a new HR employee record for <strong>{selectedCandidate?.name}</strong> and lock this candidate record.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Employee ID <span className="text-red-500">*</span>
              </label>
              <Input
                value={convertData.employee_id}
                onChange={(e) => setConvertData(prev => ({ ...prev, employee_id: e.target.value }))}
                placeholder="e.g. EMP-101"
                data-testid="convert-employee-id-input"
              />
              <p className="text-xs text-slate-400 mt-1">Must be unique — stored exactly as entered</p>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">
                Date of Joining <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={convertData.date_of_joining}
                onChange={(e) => setConvertData(prev => ({ ...prev, date_of_joining: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Work Location</label>
              <Select value={convertData.work_location} onValueChange={(v) => setConvertData(prev => ({ ...prev, work_location: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="office">Office</SelectItem>
                  <SelectItem value="remote">Remote</SelectItem>
                  <SelectItem value="hybrid">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Timezone</label>
              <Select value={convertData.employee_timezone} onValueChange={(v) => setConvertData(prev => ({ ...prev, employee_timezone: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">UTC</SelectItem>
                  <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                  <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                  <SelectItem value="Asia/Kolkata">India (IST)</SelectItem>
                  <SelectItem value="Asia/Dubai">Gulf (GST)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-4">
            <Button variant="outline" onClick={() => setIsConvertModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleConvert} 
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Candidates;
